import client from 'core/axios';
import handleError from 'core/axios/handleError';
import {
  handleJSONResponse,
  handleFileDownloadResponse,
} from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

/**
 * Returns list of badges
 *
 * @param params - filters applied to list
 */
export const getBadgesList = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/badges/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Exports list of badges
 *
 * @param params - filters applied to list
 */
export const exportBadgesList = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/badges/export/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleFileDownloadResponse)
    .catch(handleError);

/**
 * Exports list of student badges
 *
 * @param version - api version
 * @param params - filters applied to list
 */
export const exportStudentBadges = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/badges/students/export/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Returns single badge
 *
 * @badgeId - id of the badge
 * @param params - filters applied
 */
export const getBadge = ({ version = 'v1', badgeId, params = {} } = {}) =>
  client
    .request({
      url: `${version}/badges/${badgeId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Creates a badge
 *
 * @param payload - { name, image, description, criteria.narrative OR criteria.link }
 */
export const createBadge = ({ version = 'v1', payload = {} } = {}) =>
  client
    .request({
      url: `${version}/badges/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Updates a badge
 *
 * @param badgeId - id of the badge
 * @param payload - { name, image, description, criteria.narrative OR criteria.link }
 */
export const updateBadge = ({ version = 'v1', badgeId, payload = {} } = {}) =>
  client
    .request({
      url: `${version}/badges/${badgeId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Deletes a badge
 *
 * @param badgeId - id of the badge
 */
export const deleteBadge = ({ version = 'v1', badgeId } = {}) =>
  client
    .request({
      url: `${version}/badges/${badgeId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Returns badge student hash
 *
 * @param badgeId - id of the badge
 */
export const getBadgeHash = ({ version = 'v1', badgeId } = {}) =>
  client
    .request({
      url: `${version}/badges/${badgeId}/hash/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Returns badge student hash
 *
 * @param badgeId - id of the badge
 * @param payload - { students_hash }
 */
export const awardBadge = ({ version = 'v1', badgeId, payload = {} } = {}) =>
  client
    .request({
      url: `${version}/badges/${badgeId}/assign/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Exports badge awardees
 *
 * @param badgeId - id of the badge
 */
export const exportBadgeAwardees = ({ version = 'v1', badgeId } = {}) =>
  client
    .request({
      url: `${version}/badges/${badgeId}/students/export/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);
